import DOMPurify from 'dompurify';

/*
    Making a plain-text message readable.

    A text/plain reply chain rendered into a <pre> is a wall — every level of
    quoting, every hard wrap and every link laid out flat at the same weight.
    That is what this fixes. Nothing here invents content: it groups what is
    already there, folds the part you have read before, and makes the links you
    can already see clickable.

    Four things happen, in order:

      1. Unwrap. Mail is wrapped at ~78 columns before it is sent, and the
         browser wraps it again at a different width, which is what makes it
         ragged. RFC 3676 marks a soft wrap with a trailing space, so lines
         ending in one are joined back to a paragraph.

      2. Group by quote depth, and find the signature (`-- ` on its own line).

      3. Split into what is new and what is history. The trailing quoted run —
         and the "On <date>, <who> wrote:" line that introduces it — folds
         behind a toggle, so a twelve-deep chain opens as the four lines someone
         actually wrote.

      4. Escape, then mark up. Markdown, then bare URLs and addresses.

    Everything is escaped BEFORE any markup is added, and the result goes
    through DOMPurify anyway. This is hostile input.
*/

export interface Block {
    kind: 'text' | 'quote' | 'signature' | 'attribution';
    /** Levels of `>`. Zero for everything the sender wrote themselves. */
    depth: number;
    html: string;
}

export interface Rendered {
    /** What this sender wrote. */
    lead: Block[];
    /** The chain underneath it, hidden until asked for. */
    trailing: Block[];
}

/** Depth of `>` marks, and the line with them removed. */
function strip(line: string): { depth: number; rest: string } {
    let depth = 0;
    let i = 0;
    while (i < line.length && line[i] === '>') {
        depth++;
        i++;
        // One optional space per level, per RFC 3676.
        if (line[i] === ' ') i++;
    }
    return { depth, rest: line.slice(i) };
}

/**
 * Joins soft-wrapped lines back into paragraphs.
 *
 * A line that ends in a space is a soft wrap and continues; one that doesn't is
 * a real line break. Only lines at the same quote depth join, so a quote never
 * swallows the reply above it. Non-flowed mail has no trailing spaces and comes
 * through this untouched, which is the point — unwrapping text that was hard
 * wrapped on purpose would ruin ASCII tables and signatures.
 */
function unflow(lines: string[]): { depth: number; text: string }[] {
    const out: { depth: number; text: string }[] = [];

    for (const line of lines) {
        const { depth, rest } = strip(line);
        // RFC 3676 space-stuffing: a leading space is escaping, not content.
        const body = rest.startsWith(' ') ? rest.slice(1) : rest;
        const previous = out[out.length - 1];
        const continues = previous !== undefined && previous.depth === depth && previous.text.endsWith(' ') && body !== '';

        if (continues) previous.text += body;
        else out.push({ depth, text: body });
    }

    return out;
}

const SIGNATURE = /^--\s?$/;
/*
    "On 7 Aug 2026, at 13:05, Rick Danby wrote:" and its many cousins, including
    the Turkish form. Matched loosely on purpose: getting it wrong only means an
    extra line stays visible instead of folding, which is the harmless direction.
*/
const ATTRIBUTION = /^(on\s.+\bwrote:|.*\byazdı:|.*\bşunu yazdı:|-{2,}\s*(original message|forwarded message)\s*-{2,})\s*$/i;

function escapeHtml(raw: string): string {
    return raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Only schemes that can't run anything. Everything else stays plain text. */
function safeUrl(raw: string): string | null {
    const url = raw.trim();
    if (/^(https?:|mailto:)/i.test(url)) return url;
    if (/^www\./i.test(url)) return `https://${url}`;
    return null;
}

const link = (href: string, label: string) =>
    `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;

/*
    Markdown, because senders write it.

    A machine that turns HTML mail into a plain-text alternative usually emits
    markdown, so the text part of a newsletter arrives full of `[here](url)` and
    `**Name**`. Rendering it is what the sender meant. It runs on ALREADY
    ESCAPED text, so the only tags that can appear are the ones written here.
*/
function markdown(escaped: string): string {
    let s = escaped;

    /*
        The target may contain newlines.

        Senders hard-wrap at ~78 columns without caring what they cut, so a long
        tracking URL arrives split across three lines *inside* the parentheses.
        Requiring `[^)\s]+` gave up at the first newline and left the reader
        showing a literal `[here](https://…`. Whitespace inside the target is
        stitched back together instead — safe, because the target still has to
        begin with a scheme, so this can only match something that was a URL.
    */
    const target = String.raw`\(\s*((?:https?:|mailto:|www\.)[^)]*?)\s*(?:&quot;[^&]*&quot;\s*)?\)`;
    const stitch = (href: string) => href.replace(/\s+/g, '');

    // Images first, or the link rule would eat them. A remote image in plain
    // text is not fetched — it becomes a link, so nothing phones home on open.
    s = s.replace(new RegExp(String.raw`!\[([^\]]*)\]` + target, 'g'), (whole, alt: string, href: string) => {
        const url = safeUrl(stitch(href));
        if (!url) return whole;
        return link(url, alt.trim() ? escapeHtml(alt.trim()) : 'image');
    });

    s = s.replace(new RegExp(String.raw`\[([^\]]+)\]` + target, 'g'), (whole, label: string, href: string) => {
        const url = safeUrl(stitch(href));
        if (!url) return whole;
        // A label the sender wrapped mid-phrase reads better on one line.
        return link(url, label.replace(/\s*\n\s*/g, ' '));
    });

    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?:;]|$)/g, '$1<em>$2</em>');

    return s;
}

// Trailing punctuation is far more often the sentence's than the URL's.
const BARE_URL = /(?<![\w@./-])((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
const BARE_MAIL = /(?<![\w.+-])([\w.+-]+@[\w-]+\.[\w.-]+\w)/g;

function linkify(html: string): string {
    // Anything already inside a tag is left alone — split on tags and only
    // touch the text between them.
    return html
        .split(/(<[^>]+>)/)
        .map((piece, i) => {
            if (i % 2 === 1) return piece;
            return piece
                .replace(BARE_URL, (match) => {
                    const trimmed = match.replace(/[.,;:!?)\]]+$/, '');
                    const tail = match.slice(trimmed.length);
                    const url = safeUrl(trimmed);
                    return url ? link(url, escapeHtml(trimmed)) + tail : match;
                })
                .replace(BARE_MAIL, (match) => link(`mailto:${match}`, match));
        })
        .join('');
}

function inline(text: string, useMarkdown: boolean): string {
    const escaped = escapeHtml(text);
    return linkify(useMarkdown ? markdown(escaped) : escaped);
}

/**
 * Turns a plain-text body into blocks, with the quoted history separated out.
 *
 * `markdown` renders `[label](url)`, `**bold**`, `_italic_` and `` `code` ``.
 */
export function renderPlainText(raw: string, { markdown: useMarkdown = true } = {}): Rendered {
    const lines = unflow(raw.replace(/\r\n?/g, '\n').split('\n'));
    /*
        Grouped as raw lines and marked up per BLOCK, never per line.

        Line by line, a markdown link that a soft wrap split in two —
        `[feedback in this quick\nsurvey](https://…)` — never matches, and the
        brackets show through in the reader. Collapsing blank runs has the same
        problem: a run can only be seen once it is whole.
    */
    const groups: { kind: Block['kind']; depth: number; lines: string[] }[] = [];
    let signature = false;

    for (const { depth, text } of lines) {
        if (!signature && depth === 0 && SIGNATURE.test(text)) {
            signature = true;
            groups.push({ kind: 'signature', depth: 0, lines: [] });
            continue;
        }

        const kind: Block['kind'] = signature ? 'signature' : depth > 0 ? 'quote' : ATTRIBUTION.test(text) ? 'attribution' : 'text';
        const previous = groups[groups.length - 1];

        // An attribution is its own block so it can head the folded section.
        if (previous && previous.kind === kind && previous.depth === depth && kind !== 'attribution') {
            previous.lines.push(text);
        } else {
            groups.push({ kind, depth, lines: [text] });
        }
    }

    const blocks: Block[] = groups.map((g) => ({
        kind: g.kind,
        depth: g.depth,
        html: inline(
            g.lines
                // A line of nothing but spaces is a blank line. Quoted mail is
                // full of them (`>   `), and left as-is they defeat the collapse
                // below and open a hole in the middle of a paragraph.
                .map((line) => (line.trim() === '' ? '' : line))
                .join('\n')
                // More than one blank line in a row is padding, not structure.
                .replace(/\n{3,}/g, '\n\n')
                .replace(/^\n+|\n+$/g, ''),
            useMarkdown,
        ),
    }));
    const kept = blocks.filter((b) => b.html !== '' || b.kind === 'signature');

    /*
        Everything from the last attribution onwards is history. Without an
        attribution line, fall back to the trailing run of quotes — some clients
        quote with no introduction at all.
    */
    let cut = kept.length;
    const lastAttribution = kept.map((b) => b.kind).lastIndexOf('attribution');
    if (lastAttribution > 0) {
        cut = lastAttribution;
    } else {
        while (cut > 0) {
            const kind = kept[cut - 1]!.kind;
            if (kind === 'quote' || kind === 'signature') cut--;
            else break;
        }
    }

    // Folding away everything, or only a line or two, helps nobody.
    const trailing = kept.slice(cut);
    const worthFolding = trailing.length > 1 || trailing.some((b) => b.html.includes('\n'));
    if (cut === 0 || !worthFolding) return { lead: kept, trailing: [] };

    return { lead: kept.slice(0, cut), trailing };
}

/** Defence in depth: this markup is built here, and still checked. */
export function clean(html: string): string {
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['a', 'strong', 'em', 'code'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
}
